import axios from 'axios';
import { format } from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import { log } from '../utils/console';
import {
  normalizeIdentifierFilter,
  normalizeIdentifierSearch,
} from '../utils/identifierValidation';
import { ACTIVITY_DEFAULT_MAX_AGE_SEC } from '../utils/activityRedashCache';

// Define the proxy server URL from environment variable
const PROXY_SERVER_URL = process.env.REACT_APP_PROXY_URL || 'http://localhost:3002';
const REDASH_PROXY_TIMEOUT_MS = Number(process.env.REACT_APP_REDASH_TIMEOUT_MS || 120000);

// Create an axios instance for the proxy server
const proxyApi = axios.create({
  baseURL: PROXY_SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: REDASH_PROXY_TIMEOUT_MS,
});

// We'll need to set up the interceptor differently since we can't use hooks directly here
// Let's create a function to set up the interceptor with Auth0
export const setupRedashApiInterceptor = (getAccessTokenSilently: any, user: any) => {
  // Remove any existing interceptors
  proxyApi.interceptors.request.clear();
  
  // Add request interceptor to include Authorization header and user context
  proxyApi.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email read:organizations roles',
          },
        });
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add user email if available
        if (user?.email) {
          config.headers['X-User-Email'] = user.email;
        }
        
        // Add current page context
        if (window.location) {
          config.headers['X-Current-Page'] = window.location.pathname;
          config.headers['X-Current-URL'] = window.location.href;
        }
        
      } catch (error) {
        log('DEBUG: Failed to get Auth0 token:', error);
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Define the interface for query parameters
export interface RedashQueryParams {
  daterange: {
    start: string;
    end: string;
  };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  /** Redash cache TTL in seconds; 0 always re-executes the query */
  max_age?: number;
  /** Cap paginated activity fetch (Location Insights fallback path) */
  max_rows?: number;
}

export interface LocationClusterRow {
  location: string;
  lat: string;
  lng: string;
  devices: number;
  users: number;
  policy_actions: string;
}

export interface ActivityRangeResult {
  rows: ActivityData[];
  total: number;
  truncated: boolean;
}

// Define the interface for activity data
export interface ActivityData {
  Date: string;
  User: string;
  'Device ID': string;
  'Policy Actions': string;
  'Rule Detections': string;
  'App Version': string;
  'OS(Platform)': string;
  'OS Version': string;
  'Device Manufacturer': string;
  'Device Model': string;
  IP: string;
  Latitude: string;
  Longitude: string;
  accuracy: string;
  accuracy_level: string;
  Country: string;
  County: string;
  State: string;
  City: string;
  'Post Code': string;
  'Country Code': string;
  'State Code': string;
}

// Define the interface for overview table data
export interface OverviewTableData {
  date: string;
  'Action result': number;
  action_result: string;
}

// Define the interface for Redash query 26467 data
export interface OverviewMetricsData {
  ingredient_count: number;
  recipe_count: number;
  device_check_count: number;
  device_uid_count: number;
}

// Define the interface for Redash query 26468 data
export interface UsersMetricsData {
  users: number;
}

// Define the interface for Redash query 26469 data
export interface DevicesMetricsData {
  ingredient_count: number;
  recipe_count: number;
  device_check_count: number;
  device_uid_count: number;
}

// Define the interface for active policies metrics data
export interface ActivePoliciesMetricsData {
  Policies: number;
}

// Define the interface for active rules metrics data
export interface ActiveRulesMetricsData {
  ingredient_count: number;
  recipe_count: number;
  device_check_count: number;
  device_uid_count: number;
}

// Define the interface for Redash query 26473 data (Rule Distribution)
export interface RuleDistributionData {
  name: string;
  count: number;
}

// Define the interface for Redash query 26474 data (Policy Details)
export interface PolicyDetailsData {
  'Recipe ingredient': string;
  'Recipes': string;
  'Platform': string;
}

// Define the interface for Redash query 26428 data (Rule Descriptions)
export interface RuleDescriptionsData {
  'Rule description': string;
}

// Define the interface for Redash query 38551 data (Additional Rule Descriptions)
export interface AdditionalRuleDescriptionsData {
  'Rule description': string;
}

// Define the interface for Device UID data (query 26484)
export interface DeviceUIDData {
  device_uid: string;
  id?: string;
}

// Define the interface for User ID data (query 26485)
export interface UserIDData {
  value: string;
  name: string;
  id?: string;
}

// Define the interface for User Lookup data
export interface UserLookupData {
  Date: string;
  User: string;
  'Device ID': string;
  'Policy Actions': string;
  'Rule Detections': string;
  'App Version': string;
  Platform: string;
  'OS Version': string;
  'Device Manufacturer': string;
  'Device Model': string;
  IP: string;
  Latitude: string;
  Longitude: string;
  'Accuracy (m)': string;
  Confidence: string;
  Country: string;
  County: string;
  State: string;
  City: string;
  'Post Code': string;
}

// Define interface for multiple accounts data
export interface MultipleAccountsData {
  user_id: string;
  account_count: number;
  risk_level: string;
  locations: Array<{
    latitude: string;
    longitude: string;
    count: number;
  }>;
  patterns: Array<{
    pattern: string;
    count: number;
  }>;
}

// Get the query ID from environment variables or use a default
const ACTIVITY_QUERY = process.env.REACT_APP_REDASH_ACTIVITY_QUERY_ID || '';
const ACTIVITY_LIST_QUERY = process.env.REACT_APP_REDASH_ACTIVITY_LIST_QUERY_ID || '';
const ACTIVITY_LIST_COUNT_QUERY = process.env.REACT_APP_REDASH_ACTIVITY_LIST_COUNT_QUERY_ID || '';

/** Newest N activity rows exposed in the list table, count, and export. */
export const ACTIVITY_MAX_ROWS = 5000;
const LOCATION_CLUSTERS_QUERY = process.env.REACT_APP_REDASH_LOCATION_CLUSTERS_QUERY_ID || '';
const USER_LOOKUP_QUERY = process.env.REACT_APP_REDASH_USER_LOOKUP_QUERY_ID || '';
const ACTIVITY_FRAUD_RANK_QUERY = process.env.REACT_APP_REDASH_ACTIVITY_FRAUD_RANK_QUERY_ID || '';

/** Highest-ranked devices/users returned by activity_fraud_rank.sql. */
export const ACTIVITY_FRAUD_RANK_TOP_K = 500;
const OVERVIEW_QUERY = process.env.REACT_APP_REDASH_OVERVIEW_QUERY_ID || '';
const OVERVIEW_METRICS_QUERY = process.env.REACT_APP_REDASH_OVERVIEW_METRICS_QUERY_ID || '';
const OVERVIEW_SHARING_QUERY = process.env.REACT_APP_REDASH_OVERVIEW_SHARING_QUERY_ID || '';
/** Redash result cache TTL for Overview queries (seconds). */
export const OVERVIEW_DEFAULT_MAX_AGE = 300;
const RULES_QUERY = process.env.REACT_APP_REDASH_RULES_QUERY_ID || '';

export interface OverviewDashboardMetrics {
  users: number;
  devices: number;
  device_sharing_users: number;
}

export interface OverviewChartRow {
  day: string;
  action_result: string;
  cnt: number;
}

export interface OverviewSharingRow {
  date: string;
  user: string;
  device_uid: string;
  latitude: number | string | null;
  longitude: number | string | null;
}

export interface OverviewDashboardData {
  metrics: OverviewDashboardMetrics;
  chart: OverviewChartRow[];
  sharing: OverviewSharingRow[];
}

export interface OverviewDashboardParams {
  daterange: {
    start: string;
    end: string;
  };
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  max_age?: number;
}

const parseRedashJsonColumn = <T>(value: unknown, fallback: T): T => {
  if (value == null || value === '') {
    return fallback;
  }
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
};

const getOverviewTimeoutMessage = (error: any): string | null => {
  const status = error?.response?.status;
  const errorMessage = String(error?.response?.data?.message || error?.message || '');
  const isTimeout =
    error?.code === 'ECONNABORTED' ||
    status === 504 ||
    errorMessage.toLowerCase().includes('timed out');
  const isServerOverload = status === 500 || status === 502 || status === 503;

  if (isTimeout) {
    return 'This report is taking longer than expected. Please try again in a moment. If the problem persists, contact support.';
  }
  if (isServerOverload) {
    return 'The platform is experiencing high load. Please try again in a moment. If the problem persists, contact support.';
  }
  return null;
};

const environmentVariables = {
  REACT_APP_REDASH_ACTIVITY_QUERY_ID: process.env.REACT_APP_REDASH_ACTIVITY_QUERY_ID,
  NODE_ENV: process.env.NODE_ENV,
};

interface FormattedQueryParams {
  daterange: {
    start: string;
    end: string;
  };
  'Policy Action': string;
  Platform: string;
  device_uid: string;
  user_id: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
}

// Function to test the connection to Redash
export const testRedashConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await proxyApi.get('/api/test-connection');
    return response.data;
  } catch (error: any) {
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to connect to Redash' 
    };
  }
};

const buildOverviewQueryParameters = (params: OverviewDashboardParams) => ({
  daterange: {
    start: `${params.daterange.start} 00:00:00`,
    end: `${params.daterange.end} 23:59:59`,
  },
  api_key_android: params.api_key_android || '',
  api_key_ios: params.api_key_ios || '',
  api_key_web: params.api_key_web || '',
});

const parseOverviewMetricsRow = (
  row: Record<string, unknown> | null
): Pick<OverviewDashboardData, 'metrics' | 'chart'> => {
  if (!row) {
    return {
      metrics: { users: 0, devices: 0, device_sharing_users: 0 },
      chart: [],
    };
  }

  const metrics = parseRedashJsonColumn<OverviewDashboardMetrics>(row.metrics, {
    users: 0,
    devices: 0,
    device_sharing_users: 0,
  });
  const chart = parseRedashJsonColumn<OverviewChartRow[]>(row.chart, []);

  return {
    metrics: {
      users: Number(metrics.users || 0),
      devices: Number(metrics.devices || 0),
      device_sharing_users: Number(metrics.device_sharing_users || 0),
    },
    chart: Array.isArray(chart) ? chart : [],
  };
};

const parseOverviewSharingRows = (row: Record<string, unknown> | null): OverviewSharingRow[] => {
  if (!row) {
    return [];
  }

  const sharing = parseRedashJsonColumn<OverviewSharingRow[]>(row.sharing, []);
  return Array.isArray(sharing) ? sharing : [];
};

/** Fetch overview KPIs and chart only (split fast path). */
export const fetchOverviewMetrics = async (
  params: OverviewDashboardParams
): Promise<Pick<OverviewDashboardData, 'metrics' | 'chart'>> => {
  if (!OVERVIEW_METRICS_QUERY) {
    throw new Error(
      'Overview metrics query is not configured (REACT_APP_REDASH_OVERVIEW_METRICS_QUERY_ID)'
    );
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: OVERVIEW_METRICS_QUERY,
      parameters: buildOverviewQueryParameters(params),
      max_age: params.max_age ?? OVERVIEW_DEFAULT_MAX_AGE,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch overview metrics');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    return parseOverviewMetricsRow(row);
  } catch (error: unknown) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

/** Fetch device-sharing rows for map/list (split lazy path). */
export const fetchOverviewSharing = async (
  params: OverviewDashboardParams
): Promise<OverviewSharingRow[]> => {
  if (!OVERVIEW_SHARING_QUERY) {
    throw new Error(
      'Overview sharing query is not configured (REACT_APP_REDASH_OVERVIEW_SHARING_QUERY_ID)'
    );
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: OVERVIEW_SHARING_QUERY,
      parameters: buildOverviewQueryParameters(params),
      max_age: params.max_age ?? OVERVIEW_DEFAULT_MAX_AGE,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch overview sharing data');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    return parseOverviewSharingRows(row);
  } catch (error: unknown) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

// Function to fetch Overview dashboard data (aggregated single-row JSON from overview Redash query)
export const fetchOverviewDashboard = async (
  params: OverviewDashboardParams
): Promise<OverviewDashboardData> => {
  if (!OVERVIEW_QUERY) {
    throw new Error('Overview query is not configured (REACT_APP_REDASH_OVERVIEW_QUERY_ID)');
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: OVERVIEW_QUERY,
      parameters: buildOverviewQueryParameters(params),
      max_age: params.max_age ?? OVERVIEW_DEFAULT_MAX_AGE,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch overview dashboard data');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      return {
        metrics: { users: 0, devices: 0, device_sharing_users: 0 },
        chart: [],
        sharing: [],
      };
    }

    const { metrics, chart } = parseOverviewMetricsRow(row);
    const sharing = parseOverviewSharingRows(row);

    return {
      metrics,
      chart,
      sharing,
    };
  } catch (error: any) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

export const isOverviewDashboardQueryConfigured = (): boolean => Boolean(OVERVIEW_QUERY);

export const isOverviewSplitQueryConfigured = (): boolean =>
  Boolean(OVERVIEW_METRICS_QUERY && OVERVIEW_SHARING_QUERY);

export const isOverviewOptimizedQueryConfigured = (): boolean =>
  isOverviewSplitQueryConfigured() || isOverviewDashboardQueryConfigured();

export interface RulesDistributionRow {
  rule_label: string;
  cnt: number;
}

export interface RulesDashboardData {
  chart: OverviewChartRow[];
  rule_distribution: RulesDistributionRow[];
}

export interface RulesDashboardParams {
  daterange: {
    start: string;
    end: string;
  };
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  max_age?: number;
}

export const fetchRulesDashboard = async (
  params: RulesDashboardParams
): Promise<RulesDashboardData> => {
  if (!RULES_QUERY) {
    throw new Error('Rules query is not configured (REACT_APP_REDASH_RULES_QUERY_ID)');
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: RULES_QUERY,
      parameters: {
        daterange: {
          start: `${params.daterange.start} 00:00:00`,
          end: `${params.daterange.end} 23:59:59`,
        },
        api_key_android: params.api_key_android || '',
        api_key_ios: params.api_key_ios || '',
        api_key_web: params.api_key_web || '',
      },
      max_age: params.max_age ?? 900,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch rules dashboard data');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      return { chart: [], rule_distribution: [] };
    }

    const chart = parseRedashJsonColumn<OverviewChartRow[]>(row.chart, []);
    const ruleDistribution = parseRedashJsonColumn<RulesDistributionRow[]>(row.rule_distribution, []);

    return {
      chart: Array.isArray(chart) ? chart : [],
      rule_distribution: Array.isArray(ruleDistribution) ? ruleDistribution : [],
    };
  } catch (error: any) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

export const isRulesDashboardQueryConfigured = (): boolean => Boolean(RULES_QUERY);

export interface ActivityListParams {
  daterange: {
    start: string;
    end: string;
  };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  device_uid?: string;
  user_id?: string;
  page?: number;
  page_size?: number;
  skip_count?: boolean;
  max_rows?: number;
  max_age?: number;
}

export interface ActivityListResult {
  total: number | null;
  rows: ActivityData[];
}

export interface ActivityListCountParams {
  daterange: {
    start: string;
    end: string;
  };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  device_uid?: string;
  user_id?: string;
  max_rows?: number;
  max_age?: number;
}

export interface ActivityFraudRankEntry {
  deviceId?: string;
  userId?: string;
  users?: string[];
  devices?: string[];
  events: number;
  fraudScore: number;
  accountRisk: number;
  locationRisk: number;
  deviceRisk: number;
  networkRisk: number;
}

export interface ActivityFraudRankResult {
  device_rank: ActivityFraudRankEntry[];
  user_rank: ActivityFraudRankEntry[];
}

export interface ActivityFraudRankParams {
  daterange: {
    start: string;
    end: string;
  };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  top_k?: number;
  max_age?: number;
}

const buildActivityFilterParameters = (params: {
  daterange: { start: string; end: string };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
}) => ({
  daterange: {
    start: `${params.daterange.start} 00:00:00`,
    end: `${params.daterange.end} 23:59:59`,
  },
  'Policy Action': params['Policy Action'],
  Platform: params.Platform || 'ALL',
  api_key_android: params.api_key_android || '',
  api_key_ios: params.api_key_ios || '',
  api_key_web: params.api_key_web || '',
});

const buildActivityFraudRankQueryParameters = (params: ActivityFraudRankParams) => ({
  ...buildActivityFilterParameters(params),
  top_k: params.top_k ?? ACTIVITY_FRAUD_RANK_TOP_K,
});

const buildActivityListQueryParameters = (params: {
  daterange: { start: string; end: string };
  'Policy Action': string;
  Platform: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  device_uid?: string;
  user_id?: string;
  page?: number;
  page_size?: number;
  skip_count?: boolean;
  max_rows?: number;
}) => ({
  ...buildActivityFilterParameters(params),
  device_uid: normalizeIdentifierFilter(params.device_uid, 'Device UID'),
  user_id: normalizeIdentifierFilter(params.user_id, 'User ID'),
  page: params.page ?? 0,
  page_size: params.page_size ?? 50,
  skip_count: params.skip_count ? '1' : '0',
  max_rows: params.max_rows ?? ACTIVITY_MAX_ROWS,
});

export interface UserLookupListParams extends ActivityListParams {
  device_uid_filter?: string;
  user_id_filter?: string;
}

const buildUserLookupListQueryParameters = (params: UserLookupListParams) => ({
  ...buildActivityFilterParameters(params),
  device_uid_filter: normalizeIdentifierFilter(params.device_uid_filter, 'Device UID'),
  user_id_filter: normalizeIdentifierFilter(params.user_id_filter, 'User ID'),
  page: params.page ?? 0,
  page_size: params.page_size ?? 50,
});

const isRedashParamIncompatibleError = (error: unknown): boolean => {
  const message =
    (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
      ?.message ||
    (error as { message?: string })?.message ||
    '';
  return message.includes('incompatible with their definitions');
};

const filterActivityRowsByDeviceAndUser = (
  rows: ActivityData[],
  deviceUID?: string,
  userID?: string
): ActivityData[] => {
  let filtered = rows;
  if (deviceUID && deviceUID !== 'ANY') {
    filtered = filtered.filter(
      (row) => String(row['Device ID']) === String(deviceUID)
    );
  }
  if (userID && userID !== 'ANY') {
    filtered = filtered.filter((row) => String(row.User) === String(userID));
  }
  return filtered;
};

const executePaginatedListQuery = async (
  queryId: string,
  parameters: Record<string, unknown>,
  max_age?: number
): Promise<ActivityListResult> => {
  const response = await proxyApi.post('/api/execute-query', {
    query: queryId,
    parameters,
    max_age: max_age ?? 0,
  });

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch paginated activity rows');
  }

  const rows = response.data.data || [];
  const row = Array.isArray(rows) ? rows[0] : null;
  if (!row) {
    return { total: 0, rows: [] };
  }

  const listRows = parseRedashJsonColumn<ActivityData[]>(row.rows, []);
  return {
    total: Number(row.total || 0),
    rows: Array.isArray(listRows) ? listRows : [],
  };
};

const fetchAllPaginatedRows = async (
  fetchPage: (page: number) => Promise<ActivityListResult>,
  maxRows?: number,
  resolveTotal?: () => Promise<number>
): Promise<ActivityRangeResult> => {
  const pageSize = 500;
  const firstPage = await fetchPage(0);
  const rows = [...firstPage.rows];
  let total = firstPage.total;
  if (total == null && resolveTotal) {
    total = await resolveTotal();
  }
  total = total ?? rows.length;

  if (maxRows != null && rows.length >= maxRows) {
    return {
      rows: rows.slice(0, maxRows),
      total,
      truncated: total > maxRows,
    };
  }

  if (total <= rows.length) {
    return { rows, total, truncated: false };
  }

  let page = 1;
  const maxPages = Math.ceil(total / pageSize) + 1;
  while (rows.length < total && page < maxPages) {
    if (maxRows != null && rows.length >= maxRows) {
      return {
        rows: rows.slice(0, maxRows),
        total,
        truncated: true,
      };
    }

    const nextPage = await fetchPage(page);
    if (!nextPage.rows.length) break;
    rows.push(...nextPage.rows);
    page += 1;
  }

  const capped = maxRows != null ? rows.slice(0, maxRows) : rows;
  return {
    rows: capped,
    total,
    truncated: maxRows != null && (total > maxRows || rows.length > maxRows),
  };
};

const mapActivityRowToUserLookup = (row: ActivityData): UserLookupData => ({
  Date: row.Date,
  User: row.User,
  'Device ID': row['Device ID'],
  'Policy Actions': row['Policy Actions'],
  'Rule Detections': row['Rule Detections'],
  'App Version': row['App Version'],
  Platform: row['OS(Platform)'] || (row as { OS?: string }).OS || '',
  'OS Version': row['OS Version'],
  'Device Manufacturer': row['Device Manufacturer'],
  'Device Model': row['Device Model'],
  IP: row.IP,
  Latitude: row.Latitude,
  Longitude: row.Longitude,
  'Accuracy (m)': row.accuracy ?? '',
  Confidence: row.accuracy_level ?? '',
  Country: row.Country ?? '',
  County: row.County ?? '',
  State: row.State ?? '',
  City: row.City ?? '',
  'Post Code': row['Post Code'] ?? '',
});

const fetchAllActivityListRows = async (
  params: {
    startDate: string;
    endDate: string;
    deviceUID?: string;
    userID?: string;
    api_key_android?: string;
    api_key_ios?: string;
    api_key_web?: string;
    max_age?: number;
  },
  overrides?: { deviceUID?: string; userID?: string }
): Promise<ActivityData[]> => {
  const pageSize = 500;
  const listParams: ActivityListParams = {
    daterange: {
      start: params.startDate,
      end: params.endDate,
    },
    'Policy Action': 'ALL',
    Platform: 'ALL',
    api_key_android: params.api_key_android,
    api_key_ios: params.api_key_ios,
    api_key_web: params.api_key_web,
    device_uid: overrides?.deviceUID ?? params.deviceUID ?? 'ANY',
    user_id: overrides?.userID ?? params.userID ?? 'ANY',
    page_size: pageSize,
    max_age: params.max_age ?? 0,
  };

  const result = await fetchAllPaginatedRows((page) =>
    fetchActivityList({ ...listParams, page })
  );
  return result.rows;
};

const fetchAllUserLookupListRows = async (
  params: {
    startDate: string;
    endDate: string;
    deviceUID?: string;
    userID?: string;
    api_key_android?: string;
    api_key_ios?: string;
    api_key_web?: string;
    max_age?: number;
  },
  overrides?: { deviceUID?: string; userID?: string }
): Promise<ActivityData[]> => {
  if (!USER_LOOKUP_QUERY) {
    throw new Error('User lookup query is not configured (REACT_APP_REDASH_USER_LOOKUP_QUERY_ID)');
  }

  const pageSize = 500;
  const listParams: UserLookupListParams = {
    daterange: {
      start: params.startDate,
      end: params.endDate,
    },
    'Policy Action': 'ALL',
    Platform: 'ALL',
    api_key_android: params.api_key_android,
    api_key_ios: params.api_key_ios,
    api_key_web: params.api_key_web,
    device_uid_filter: overrides?.deviceUID ?? params.deviceUID ?? 'ANY',
    user_id_filter: overrides?.userID ?? params.userID ?? 'ANY',
    page_size: pageSize,
    max_age: params.max_age ?? 0,
  };

  const result = await fetchAllPaginatedRows((page) =>
    executePaginatedListQuery(
      USER_LOOKUP_QUERY,
      buildUserLookupListQueryParameters({ ...listParams, page }),
      listParams.max_age
    ).catch((error: unknown) => {
      const timeoutMessage = getOverviewTimeoutMessage(error);
      if (timeoutMessage) {
        throw new Error(timeoutMessage);
      }
      const redashMessage = (error as { response?: { data?: { message?: string } } })?.response
        ?.data?.message;
      if (redashMessage) {
        throw new Error(redashMessage);
      }
      throw error;
    })
  );
  return result.rows;
};

const fetchUserLookupActivityRows = async (params: {
  startDate: string;
  endDate: string;
  deviceUID?: string;
  userID?: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  max_age?: number;
}): Promise<ActivityData[]> => {
  const deviceUID = params.deviceUID || 'ANY';
  const userID = params.userID || 'ANY';
  const fetchRows = USER_LOOKUP_QUERY
    ? fetchAllUserLookupListRows
    : ACTIVITY_LIST_QUERY
      ? fetchAllActivityListRows
      : null;

  if (!fetchRows) {
    throw new Error('No user lookup list query configured');
  }

  try {
    return await fetchRows(params);
  } catch (error) {
    if (
      !isRedashParamIncompatibleError(error)
      || (deviceUID === 'ANY' && userID === 'ANY')
    ) {
      throw error;
    }

    // Redash dropdown params reject pasted base64 device UIDs — refetch unfiltered and filter in-app.
    const rows = await fetchRows(params, { deviceUID: 'ANY', userID: 'ANY' });
    return filterActivityRowsByDeviceAndUser(rows, deviceUID, userID);
  }
};

export const isLocationClustersQueryConfigured = (): boolean => !!LOCATION_CLUSTERS_QUERY;

/** Fetch pre-aggregated location clusters (Location Insights fast path). */
export const fetchLocationClusters = async (
  params: RedashQueryParams
): Promise<LocationClusterRow[]> => {
  if (!LOCATION_CLUSTERS_QUERY) {
    throw new Error(
      'Location clusters query is not configured (REACT_APP_REDASH_LOCATION_CLUSTERS_QUERY_ID)'
    );
  }

  const maxAge = params.max_age ?? ACTIVITY_DEFAULT_MAX_AGE_SEC;

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: LOCATION_CLUSTERS_QUERY,
      parameters: buildActivityFilterParameters(params),
      max_age: maxAge,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch location clusters');
    }

    const rows = response.data.data || [];
    return (Array.isArray(rows) ? rows : []).map((row: Record<string, unknown>) => ({
      location: String(row.location ?? ''),
      lat: String(row.lat ?? ''),
      lng: String(row.lng ?? ''),
      devices: Number(row.devices ?? 0),
      users: Number(row.users ?? 0),
      policy_actions: String(row.policy_actions ?? ''),
    }));
  } catch (error: unknown) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

/** Fetch activity rows for a date range (paginated list when configured, else legacy). */
export const fetchAllActivityDataForRange = async (
  params: RedashQueryParams
): Promise<ActivityRangeResult> => {
  const maxAge = params.max_age ?? ACTIVITY_DEFAULT_MAX_AGE_SEC;
  const maxRows = params.max_rows ?? ACTIVITY_MAX_ROWS;

  if (ACTIVITY_LIST_QUERY) {
    const pageSize = 500;
    const listParams: ActivityListParams = {
      daterange: params.daterange,
      'Policy Action': params['Policy Action'],
      Platform: params.Platform || 'ALL',
      api_key_android: params.api_key_android,
      api_key_ios: params.api_key_ios,
      api_key_web: params.api_key_web,
      device_uid: 'ANY',
      user_id: 'ANY',
      page_size: pageSize,
      max_rows: maxRows,
      max_age: maxAge,
      skip_count: isActivityListCountQueryConfigured(),
    };

    const countParams: ActivityListCountParams = {
      daterange: params.daterange,
      'Policy Action': params['Policy Action'],
      Platform: params.Platform || 'ALL',
      api_key_android: params.api_key_android,
      api_key_ios: params.api_key_ios,
      api_key_web: params.api_key_web,
      device_uid: 'ANY',
      user_id: 'ANY',
      max_rows: maxRows,
      max_age: maxAge,
    };

    return fetchAllPaginatedRows(
      (page) => fetchActivityList({ ...listParams, page }),
      maxRows,
      isActivityListCountQueryConfigured()
        ? () => fetchActivityListCount(countParams).then((result) => result.total)
        : undefined
    );
  }

  const rows = await fetchActivityData({ ...params, max_age: maxAge });
  const total = rows.length;
  if (maxRows != null && total > maxRows) {
    return { rows: rows.slice(0, maxRows), total, truncated: true };
  }
  return { rows, total, truncated: false };
};

export const fetchActivityList = async (
  params: ActivityListParams
): Promise<ActivityListResult> => {
  if (!ACTIVITY_LIST_QUERY) {
    throw new Error('Activity list query is not configured (REACT_APP_REDASH_ACTIVITY_LIST_QUERY_ID)');
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: ACTIVITY_LIST_QUERY,
      parameters: buildActivityListQueryParameters(params),
      max_age: params.max_age ?? ACTIVITY_DEFAULT_MAX_AGE_SEC,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch activity list');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      return { total: 0, rows: [] };
    }

    const listRows = parseRedashJsonColumn<ActivityData[]>(row.rows, []);
    const rawTotal = row.total;
    return {
      total: rawTotal == null || rawTotal === '' ? null : Number(rawTotal),
      rows: Array.isArray(listRows) ? listRows : [],
    };
  } catch (error: any) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

const buildActivityListCountQueryParameters = (params: ActivityListCountParams) => ({
  ...buildActivityFilterParameters(params),
  device_uid: normalizeIdentifierFilter(params.device_uid, 'Device UID'),
  user_id: normalizeIdentifierFilter(params.user_id, 'User ID'),
  max_rows: params.max_rows ?? ACTIVITY_MAX_ROWS,
});

export const fetchActivityListCount = async (
  params: ActivityListCountParams
): Promise<{ total: number }> => {
  if (!ACTIVITY_LIST_COUNT_QUERY) {
    throw new Error(
      'Activity list count query is not configured (REACT_APP_REDASH_ACTIVITY_LIST_COUNT_QUERY_ID)'
    );
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: ACTIVITY_LIST_COUNT_QUERY,
      parameters: buildActivityListCountQueryParameters(params),
      max_age: params.max_age ?? ACTIVITY_DEFAULT_MAX_AGE_SEC,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch activity list count');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    return { total: Number(row?.total || 0) };
  } catch (error: any) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

export const fetchActivityFraudRank = async (
  params: ActivityFraudRankParams
): Promise<ActivityFraudRankResult> => {
  if (!ACTIVITY_FRAUD_RANK_QUERY) {
    throw new Error('Activity fraud rank query is not configured (REACT_APP_REDASH_ACTIVITY_FRAUD_RANK_QUERY_ID)');
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: ACTIVITY_FRAUD_RANK_QUERY,
      parameters: buildActivityFraudRankQueryParameters(params),
      max_age: params.max_age ?? ACTIVITY_DEFAULT_MAX_AGE_SEC,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch activity fraud rank');
    }

    const rows = response.data.data || [];
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row) {
      return { device_rank: [], user_rank: [] };
    }

    const deviceRank = parseRedashJsonColumn<ActivityFraudRankEntry[]>(row.device_rank, []);
    const userRank = parseRedashJsonColumn<ActivityFraudRankEntry[]>(row.user_rank, []);

    return {
      device_rank: Array.isArray(deviceRank) ? deviceRank : [],
      user_rank: Array.isArray(userRank) ? userRank : [],
    };
  } catch (error: any) {
    const timeoutMessage = getOverviewTimeoutMessage(error);
    if (timeoutMessage) {
      throw new Error(timeoutMessage);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

export const isActivityListQueryConfigured = (): boolean => Boolean(ACTIVITY_LIST_QUERY);
export const isActivityListCountQueryConfigured = (): boolean => Boolean(ACTIVITY_LIST_COUNT_QUERY);
export const isUserLookupQueryConfigured = (): boolean => Boolean(USER_LOOKUP_QUERY);

export const isActivityFraudRankQueryConfigured = (): boolean => Boolean(ACTIVITY_FRAUD_RANK_QUERY);

export const isActivityOptimizedQueryConfigured = (): boolean =>
  Boolean(ACTIVITY_LIST_QUERY && ACTIVITY_FRAUD_RANK_QUERY);

// Function to fetch activity data from Redash
export const fetchActivityData = async (params: RedashQueryParams): Promise<ActivityData[]> => {
  try {
    
    // Format the parameters to match Redash's expected format
    const formattedParams: FormattedQueryParams = {
      daterange: {
        start: `${params.daterange.start} 00:00:00`,
        end: `${params.daterange.end} 23:59:59`
      },
      'Policy Action': params['Policy Action'],
      Platform: params.Platform || 'ALL',
      device_uid: 'ANY',
      user_id: 'ANY',
      api_key_android: params.api_key_android || '',
      api_key_ios: params.api_key_ios || '',
      api_key_web: params.api_key_web || ''
    };
    
    const response = await proxyApi.post('/api/execute-query', {
      query: ACTIVITY_QUERY,
      parameters: formattedParams,
      max_age: params.max_age ?? 0,
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch activity data');
    }
    
    // Ensure we always return an array, even if empty
    const data = response.data.data || [];
    if (!Array.isArray(data)) {
      console.error('Expected array but got:', typeof data);
      return [];
    }
    
    return data;
  } catch (error: any) {
    const message = getOverviewTimeoutMessage(error);
    if (message) {
      throw new Error(message);
    }

    // Keep technical details in console for debugging, but don't break UI consumers.
    console.warn('Error fetching activity data:', error);
    return [];
  }
};

// Function to fetch rule distribution data from Redash
export const fetchRuleDistributionData = async (): Promise<RuleDistributionData[]> => {
  try {
    
    // Calculate date range for last 7 days to today
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formattedStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
    const formattedEndDate = format(today, 'yyyy-MM-dd');
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '26473',
      parameters: {
        daterange: {
          start: `${formattedStartDate} 00:00:00`,
          end: `${formattedEndDate} 23:59:59`
        }
      },
      max_age: 1800  // Cache for 30 minutes
    });
    
    // Ensure we always return an array, even if empty
    const data = response.data.data || [];
    if (!Array.isArray(data)) {
      console.error('Expected array but got:', typeof data);
      return [];
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching rule distribution data:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
};

// Function to fetch policy details data from Redash
export const fetchPolicyDetailsData = async (): Promise<PolicyDetailsData[]> => {
  try {
    
    // Calculate date range for last 7 days to today
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formattedStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
    const formattedEndDate = format(today, 'yyyy-MM-dd');
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '26474',
      parameters: {
        daterange: {
          start: `${formattedStartDate} 00:00:00`,
          end: `${formattedEndDate} 23:59:59`
        }
      },
      max_age: 1800  // Cache for 30 minutes
    });
    
    // Ensure we always return an array, even if empty
    const data = response.data.data || [];
    if (!Array.isArray(data)) {
      console.error('Expected array but got:', typeof data);
      return [];
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching policy details data:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
};

// Function to fetch rule descriptions data from Redash
export const fetchRuleDescriptionsData = async (): Promise<RuleDescriptionsData[]> => {
  try {
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '26428',
      parameters: {},
      max_age: 0  // Don't use cache, always execute
    });
    
    // Ensure we always return an array, even if empty
    const data = response.data.data || [];
    if (!Array.isArray(data)) {
      console.error('Expected array but got:', typeof data);
      return [];
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching rule descriptions data:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
};

// Function to fetch additional rule descriptions data from Redash
export const fetchAdditionalRuleDescriptionsData = async (): Promise<AdditionalRuleDescriptionsData[]> => {
  try {
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '38551',
      parameters: {},
      max_age: 0  // Don't use cache, always execute
    });
    
    // Ensure we always return an array, even if empty
    const data = response.data.data || [];
    if (!Array.isArray(data)) {
      console.error('Expected array but got:', typeof data);
      return [];
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching additional rule descriptions data:', error);
    // Return empty array instead of throwing to prevent UI from breaking
    return [];
  }
};

const DEVICE_UID_SEARCH_QUERY = process.env.REACT_APP_REDASH_DEVICE_UID_SEARCH_QUERY_ID || '';
const USER_ID_SEARCH_QUERY = process.env.REACT_APP_REDASH_USER_ID_SEARCH_QUERY_ID || '';

export const isDeviceUidSearchConfigured = (): boolean => Boolean(DEVICE_UID_SEARCH_QUERY);
export const isUserIdSearchConfigured = (): boolean => Boolean(USER_ID_SEARCH_QUERY);

const apiKeyParams = (params?: {
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
}) => ({
  api_key_android: params?.api_key_android || '',
  api_key_ios: params?.api_key_ios || '',
  api_key_web: params?.api_key_web || '',
});

// Function to fetch Device UID data from Redash (legacy full list)
export const fetchDeviceUIDData = async (params?: {
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
}): Promise<DeviceUIDData[]> => {
  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: process.env.REACT_APP_REDASH_DEVICE_ID_QUERY_ID || '43573',
      parameters: apiKeyParams(params),
      max_age: 0
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch Device UID data');
    }

    return response.data.data || [];
  } catch (error: any) {
    console.error('Error fetching Device UID data:', error);
    return [];
  }
};

// Function to fetch User ID data from Redash (legacy full list)
export const fetchUserIDData = async (params?: {
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
}): Promise<UserIDData[]> => {
  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: process.env.REACT_APP_REDASH_USER_ID_QUERY_ID || '43572',
      parameters: apiKeyParams(params),
      max_age: 0
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch User ID data');
    }

    return response.data.data || [];
  } catch (error: any) {
    console.error('Error fetching User ID data:', error);
    return [];
  }
};

export const searchDeviceUIDs = async (params: {
  search: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  result_limit?: number;
  max_age?: number;
}): Promise<DeviceUIDData[]> => {
  let term: string;
  try {
    term = normalizeIdentifierSearch(params.search);
  } catch {
    return [];
  }
  if (!term) return [];

  if (!DEVICE_UID_SEARCH_QUERY) {
    return [];
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: DEVICE_UID_SEARCH_QUERY,
      parameters: {
        ...apiKeyParams(params),
        search: term,
        result_limit: params.result_limit ?? 50,
      },
      max_age: params.max_age ?? 900,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to search device UIDs');
    }

    const rows = response.data.data || [];
    return rows
      .map((row: { device_uid?: string }) => row?.device_uid)
      .filter((uid: string | undefined): uid is string => Boolean(uid && uid !== 'ANY'))
      .map((device_uid: string) => ({ device_uid, id: `device-${device_uid}` }));
  } catch (error: any) {
    console.error('Error searching device UIDs:', error);
    return [];
  }
};

export const searchUserIDs = async (params: {
  search: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  result_limit?: number;
  max_age?: number;
}): Promise<UserIDData[]> => {
  let term: string;
  try {
    term = normalizeIdentifierSearch(params.search);
  } catch {
    return [];
  }
  if (!term) return [];

  if (!USER_ID_SEARCH_QUERY) {
    return [];
  }

  try {
    const response = await proxyApi.post('/api/execute-query', {
      query: USER_ID_SEARCH_QUERY,
      parameters: {
        ...apiKeyParams(params),
        search: term,
        result_limit: params.result_limit ?? 50,
      },
      max_age: params.max_age ?? 900,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to search user IDs');
    }

    const rows = response.data.data || [];
    return rows
      .map((row: { value?: string; name?: string }) => {
        const value = row?.value ?? row?.name;
        if (!value || value === 'ANY') return null;
        return { value, name: row?.name ?? value, id: `user-${value}` };
      })
      .filter((row: UserIDData | null): row is UserIDData => row !== null);
  } catch (error: any) {
    console.error('Error searching user IDs:', error);
    return [];
  }
};

// Function to fetch User Lookup data
export const fetchUserLookupData = async (params: {
  startDate: string;
  endDate: string;
  deviceUID?: string;
  userID?: string;
  api_key_android?: string;
  api_key_ios?: string;
  api_key_web?: string;
  max_age?: number;
}): Promise<UserLookupData[]> => {
  const deviceUID = normalizeIdentifierFilter(params.deviceUID, 'Device UID');
  const userID = normalizeIdentifierFilter(params.userID, 'User ID');

  try {
    if (USER_LOOKUP_QUERY || ACTIVITY_LIST_QUERY) {
      const rows = await fetchUserLookupActivityRows({
        ...params,
        deviceUID,
        userID,
      });
      return rows.map(mapActivityRowToUserLookup);
    }

    const formattedParams: any = {
      daterange: {
        start: `${params.startDate} 00:00:00`,
        end: `${params.endDate} 23:59:59`
      },
      'Policy Action': 'ALL',
      Platform: 'ALL',
      device_uid: deviceUID,
      user_id: userID,
      api_key_android: params.api_key_android || '',
      api_key_ios: params.api_key_ios || '',
      api_key_web: params.api_key_web || ''
    };

    try {
      const response = await proxyApi.post('/api/execute-query', {
        query: ACTIVITY_QUERY,
        parameters: formattedParams,
        max_age: 0
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch User Lookup data');
      }

      return response.data.data || [];
    } catch (legacyError) {
      if (
        !isRedashParamIncompatibleError(legacyError)
        || (deviceUID === 'ANY' && userID === 'ANY')
      ) {
        throw legacyError;
      }

      const response = await proxyApi.post('/api/execute-query', {
        query: ACTIVITY_QUERY,
        parameters: {
          ...formattedParams,
          device_uid: 'ANY',
          user_id: 'ANY',
        },
        max_age: 0
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch User Lookup data');
      }

      let rows = (response.data.data || []) as UserLookupData[];
      if (deviceUID !== 'ANY') {
        rows = rows.filter(
          (row) => String(row['Device ID']) === String(deviceUID)
        );
      }
      if (userID !== 'ANY') {
        rows = rows.filter((row) => String(row.User) === String(userID));
      }
      return rows;
    }
  } catch (error: any) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching User Lookup data:', error);
    }
    const redashMessage = error?.response?.data?.message;
    if (redashMessage) {
      throw new Error(redashMessage);
    }
    throw error;
  }
};

// Function to fetch active policies metrics data from Redash
export const fetchActivePoliciesMetricsData = async (): Promise<ActivePoliciesMetricsData> => {
  try {
    
    // Calculate date range for last 7 days to today
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formattedStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
    const formattedEndDate = format(today, 'yyyy-MM-dd');
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '26471',
      parameters: {
        daterange: {
          start: `${formattedStartDate} 00:00:00`,
          end: `${formattedEndDate} 23:59:59`
        }
      },
      max_age: 1800  // Cache for 30 minutes
    });
    
    // Check if we have data
    if (!response.data.data || response.data.data.length === 0) {
      return { Policies: 0 };
    }
    
    // Check if the data has the expected format
    const data = response.data.data[0];
    if (!data || typeof data.Policies !== 'number') {
      // Try to find a numeric value in the data
      const numericValues = Object.entries(data || {})
        .filter(([_, value]) => typeof value === 'number')
        .map(([key, value]) => ({ key, value: value as number }));
      
      if (numericValues.length > 0) {
        // Use the first numeric value as a fallback
        return { Policies: numericValues[0].value };
      }
      
      return { Policies: 0 };
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching active policies metrics data:', error);
    // Return default values instead of throwing to prevent UI from breaking
    return {
      Policies: 0
    };
  }
};

// Function to fetch active rules metrics data from Redash
export const fetchActiveRulesMetricsData = async (): Promise<ActiveRulesMetricsData> => {
  try {
    
    // Calculate date range for last 7 days to today
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    const formattedStartDate = format(sevenDaysAgo, 'yyyy-MM-dd');
    const formattedEndDate = format(today, 'yyyy-MM-dd');
    
    // Format the parameters to match the format used in other queries
    const response = await proxyApi.post('/api/execute-query', {
      query: '26472',
      parameters: {
        daterange: {
          start: `${formattedStartDate} 00:00:00`,
          end: `${formattedEndDate} 23:59:59`
        }
      },
      max_age: 1800  // Cache for 30 minutes
    });
    
    // Check if we have data
    if (!response.data.data || response.data.data.length === 0) {
      return {
        ingredient_count: 0,
        recipe_count: 0,
        device_check_count: 0,
        device_uid_count: 0
      };
    }
    
    // Check if the data has the expected format
    const data = response.data.data[0];
    if (!data || typeof data.ingredient_count !== 'number') {
      // Try to find numeric values in the data
      const result: ActiveRulesMetricsData = {
        ingredient_count: 0,
        recipe_count: 0,
        device_check_count: 0,
        device_uid_count: 0
      };
      
      if (data) {
        // Try to map any numeric values to our expected fields
        if (typeof data.ingredient_count === 'number') result.ingredient_count = data.ingredient_count;
        if (typeof data.recipe_count === 'number') result.recipe_count = data.recipe_count;
        if (typeof data.device_check_count === 'number') result.device_check_count = data.device_check_count;
        if (typeof data.device_uid_count === 'number') result.device_uid_count = data.device_uid_count;
        
        // If we couldn't find any of our expected fields, try to use any numeric value
        if (result.ingredient_count === 0) {
          const numericValues = Object.entries(data)
            .filter(([_, value]) => typeof value === 'number')
            .map(([key, value]) => ({ key, value: value as number }));
          
          if (numericValues.length > 0) {
            // Use the first numeric value as a fallback for ingredient_count
            result.ingredient_count = numericValues[0].value;
          }
        }
      }
      
      return result;
    }
    
    return data;
  } catch (error: any) {
    console.error('Error fetching active rules metrics data:', error);
    // Return default values instead of throwing to prevent UI from breaking
    return {
      ingredient_count: 0,
      recipe_count: 0,
      device_check_count: 0,
      device_uid_count: 0
    };
  }
}; 